import { useQuery } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { api, unwrap, type Schemas } from '../api/client.js';
import { FirstSteps } from '../components/FirstSteps.js';
import { flagOf, regionName } from '../components/geo.js';
import { GridIcon, ListIcon, PlusIcon } from '../components/icons.js';
import { PageTitle } from '../components/Layouts.js';
import {
  HealthTrend,
  PulseStrip,
  ScoreRing,
  SeverityBar,
  UptimeStrip,
  hoursUptime,
} from '../components/SiteCharts.js';
import {
  Card,
  LinkButton,
  LoadError,
  Loading,
  Notice,
  SelectField,
  StatusBadge,
  TextField,
  type MonitorState,
  type Severity,
} from '../components/ui.js';
import { errorMessage } from '../errors.js';
import { formatDateTime, formatNumber } from '../i18n.js';
import { useMe } from '../session.js';

export const SEVERITIES: Severity[] = ['critical', 'high', 'medium', 'low', 'info'];

export function useSites() {
  return useQuery({
    queryKey: ['sites'],
    queryFn: () => unwrap(api.GET('/sites')),
    refetchInterval: 60_000,
  });
}

type Site = Schemas['SiteSummary'];
type Filter = 'all' | 'problems' | 'down';
type Sort = 'priority' | 'name' | 'score' | 'problems';
type View = 'cards' | 'list';

const VIEW_KEY = 'wg.sites.view';

/** Cards or a compact list (T-79); remembered per browser, cards by default. */
function useView() {
  const [view, setView] = useState<View>(() => {
    try {
      return window.localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'cards';
    } catch {
      return 'cards';
    }
  });
  const choose = (next: View) => {
    setView(next);
    try {
      window.localStorage.setItem(VIEW_KEY, next);
    } catch {
      // not remembered: the choice holds until the page is left
    }
  };
  return [view, choose] as const;
}

const urgent = (s: Site) => s.openFindings.critical + s.openFindings.high;
const openTotal = (s: Site) => SEVERITIES.reduce((n, sev) => n + s.openFindings[sev], 0);
const isDown = (s: Site) => s.status !== 'paused' && s.uptimeState === 'down';

/** Down first, then the most urgent problems, then the lowest score; paused at the end. */
const SORTS: Record<Sort, (a: Site, b: Site) => number> = {
  priority: (a, b) =>
    Number(a.status === 'paused') - Number(b.status === 'paused') ||
    Number(isDown(b)) - Number(isDown(a)) ||
    urgent(b) - urgent(a) ||
    (a.healthScore ?? 101) - (b.healthScore ?? 101) ||
    a.name.localeCompare(b.name),
  name: (a, b) => a.name.localeCompare(b.name),
  score: (a, b) => (a.healthScore ?? 101) - (b.healthScore ?? 101),
  problems: (a, b) => openTotal(b) - openTotal(a) || urgent(b) - urgent(a),
};

/** S2: every site with its score, trends, hosting and open problems at a glance (T-55a). */
export function Overview() {
  const { t } = useTranslation();
  const sites = useSites();
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [sort, setSort] = useState<Sort>('priority');
  const [view, setView] = useView();
  const items = sites.data?.items;
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return [...(items ?? [])]
      .filter((s) => !q || s.name.toLowerCase().includes(q) || s.host.toLowerCase().includes(q))
      .filter((s) =>
        filter === 'down' ? isDown(s) : filter === 'problems' ? openTotal(s) > 0 : true,
      )
      .sort(SORTS[sort]);
  }, [items, query, filter, sort]);

  const addLink = (
    <Link
      to="/sites/new"
      className="inline-flex min-h-11 items-center gap-2 rounded-md bg-blue-700 px-4 font-semibold text-white hover:bg-blue-800"
    >
      <PlusIcon />
      {t('overview.add')}
    </Link>
  );

  let content;
  if (sites.isPending) content = <Loading />;
  else if (sites.isError)
    content = (
      <LoadError message={errorMessage(sites.error, t)} onRetry={() => void sites.refetch()} />
    );
  else if (sites.data.items.length === 0)
    content = (
      <Card>
        <h2 className="text-xl font-semibold">{t('overview.empty')}</h2>
        <p className="mt-2 text-slate-800">{t('overview.emptyText')}</p>
      </Card>
    );
  else {
    const down = sites.data.items.filter(isDown).length;
    content = (
      <>
        {down > 0 ? (
          <div className="mb-4">
            <Notice kind="error">{t('banner.openIncidents', { count: down })}</Notice>
          </div>
        ) : null}
        <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem] xl:items-start">
          <div className="xl:sticky xl:top-4 xl:order-2">
            <Situation sites={sites.data.items} />
          </div>
          <div className="min-w-0 xl:order-1">
            <div className="mb-4 grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_auto] xl:grid-cols-2 2xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_auto]">
              <TextField
                label={t('overview.search')}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
              <SelectField
                label={t('overview.filter.label')}
                value={filter}
                onChange={(e) => setFilter(e.target.value as Filter)}
              >
                {(['all', 'problems', 'down'] as const).map((f) => (
                  <option key={f} value={f}>
                    {t(`overview.filter.${f}`)}
                  </option>
                ))}
              </SelectField>
              <SelectField
                label={t('overview.sort.label')}
                value={sort}
                onChange={(e) => setSort(e.target.value as Sort)}
              >
                {(['priority', 'name', 'score', 'problems'] as const).map((s) => (
                  <option key={s} value={s}>
                    {t(`overview.sort.${s}`)}
                  </option>
                ))}
              </SelectField>
              <ViewToggle view={view} onChange={setView} />
            </div>
            {shown.length === 0 ? (
              <Notice kind="info">{t('overview.noMatch')}</Notice>
            ) : view === 'list' ? (
              <SiteTable sites={shown} />
            ) : (
              <ul className="grid gap-4 lg:grid-cols-2">
                {shown.map((site) => (
                  <li key={site.id}>
                    <SiteCard site={site} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </>
    );
  }
  return (
    <>
      <PageTitle actions={addLink}>{t('overview.title')}</PageTitle>
      <FirstSteps />
      {content}
    </>
  );
}

function ViewToggle({ view, onChange }: { view: View; onChange: (view: View) => void }) {
  const { t } = useTranslation();
  const options: [View, ReactNode][] = [
    ['cards', <GridIcon key="i" />],
    ['list', <ListIcon key="i" />],
  ];
  return (
    <div role="group" aria-label={t('overview.view.label')} className="flex flex-col gap-1">
      <span aria-hidden="true" className="font-semibold">
        {t('overview.view.label')}
      </span>
      <div className="inline-flex min-h-11 rounded-md border border-slate-400 bg-white p-1 shadow-sm">
        {options.map(([value, icon]) => (
          <button
            key={value}
            type="button"
            aria-pressed={view === value}
            onClick={() => onChange(value)}
            className={`inline-flex items-center gap-2 rounded px-3 text-sm font-semibold ${
              view === value ? 'bg-blue-700 text-white' : 'text-slate-800 hover:bg-blue-50'
            }`}
          >
            {icon}
            {t(`overview.view.${value}`)}
          </button>
        ))}
      </div>
    </div>
  );
}

/** The last 24 hours of a site as pearls, with the share of successful checks (T-88). */
function PulseCell({ site }: { site: Site }) {
  const { t, i18n } = useTranslation();
  if (site.uptimeHours.length === 0)
    return <span className="text-sm text-slate-600">{t('overview.noData')}</span>;
  const pct = hoursUptime(site.uptimeHours);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <PulseStrip hours={site.uptimeHours} />
      <span className="text-sm whitespace-nowrap text-slate-700 tabular-nums">
        {pct === null
          ? t('overview.noData')
          : t('overview.pulse.share', { pct: formatNumber(pct, i18n.language, 2) })}
      </span>
    </div>
  );
}

/** One row per site: the same facts as the card, without charts (T-79). */
function SiteTable({ sites }: { sites: readonly Site[] }) {
  const { t, i18n } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const days = (n: number | null, warn: number) =>
    n === null ? (
      <span className="text-slate-600">{t('overview.noData')}</span>
    ) : (
      <span className={n < warn ? 'font-semibold text-red-800' : undefined}>
        {t('overview.card.daysLeft', { count: n })}
      </span>
    );
  const head = 'px-3 py-2 text-left text-sm font-semibold text-slate-700';
  const cell = 'px-3 py-3 align-middle';
  const wide = 'hidden md:table-cell';
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
      <table className="w-full text-sm">
        <caption className="sr-only">{t('overview.title')}</caption>
        <thead className="border-b border-slate-200 bg-slate-50">
          <tr>
            <th scope="col" className={head}>
              {t('overview.table.site')}
            </th>
            <th scope="col" className={`${head} hidden sm:table-cell`}>
              {t('overview.table.status')}
            </th>
            <th scope="col" className={`${head} ${wide}`}>
              {t('overview.pulse.title')}
            </th>
            <th scope="col" className={`${head} hidden 2xl:table-cell`}>
              {t('overview.uptime30d')}
            </th>
            <th scope="col" className={head}>
              {t('overview.problems')}
            </th>
            <th scope="col" className={`${head} hidden lg:table-cell`}>
              {t('overview.card.ssl')}
            </th>
            <th scope="col" className={`${head} hidden lg:table-cell`}>
              {t('overview.card.response')}
            </th>
            <th scope="col" className={`${head} hidden xl:table-cell`}>
              {t('overview.table.lastCheck')}
            </th>
            <th scope="col" className={`${head} hidden sm:table-cell`}>
              <span className="sr-only">{t('overview.table.actions')}</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {sites.map((site) => {
            const total = openTotal(site);
            const hot = urgent(site);
            return (
              <tr key={site.id} className="hover:bg-blue-50/40">
                <td className={cell}>
                  <div className="flex min-w-48 items-center gap-3">
                    <ScoreRing score={site.healthScore} grade={site.healthGrade} size="sm" />
                    <div className="min-w-0">
                      <Link
                        to={`/sites/${site.id}`}
                        aria-label={t('overview.open', { name: site.name })}
                        className="font-semibold text-slate-900 hover:underline"
                      >
                        {site.name}
                      </Link>
                      <p className="break-all text-slate-600">{site.host}</p>
                      <div className="mt-1 sm:hidden">
                        <StatusBadge
                          state={site.status === 'paused' ? 'paused' : site.uptimeState}
                        />
                      </div>
                    </div>
                  </div>
                </td>
                <td className={`${cell} hidden sm:table-cell`}>
                  <StatusBadge state={site.status === 'paused' ? 'paused' : site.uptimeState} />
                </td>
                <td className={`${cell} ${wide}`}>
                  <PulseCell site={site} />
                </td>
                <td className={`${cell} hidden tabular-nums 2xl:table-cell`}>
                  {site.uptime30d === null ? (
                    <span className="text-slate-600">{t('overview.noData')}</span>
                  ) : (
                    `${formatNumber(site.uptime30d, i18n.language, 2)} %`
                  )}
                </td>
                <td className={cell}>
                  {total === 0 ? (
                    <span className="text-green-800">{t('overview.noProblems')}</span>
                  ) : (
                    <Link
                      to={`/sites/${site.id}/findings`}
                      className="inline-flex flex-wrap items-center gap-2 hover:underline"
                    >
                      {hot > 0 ? (
                        <span className="rounded bg-red-100 px-2 py-0.5 font-semibold whitespace-nowrap text-red-900">
                          {t('overview.table.urgent', { count: hot })}
                        </span>
                      ) : null}
                      <span className="whitespace-nowrap text-slate-800">
                        {t('overview.table.total', { count: total })}
                      </span>
                    </Link>
                  )}
                </td>
                <td className={`${cell} hidden lg:table-cell`}>{days(site.sslDaysLeft, 14)}</td>
                <td className={`${cell} hidden tabular-nums lg:table-cell`}>
                  {site.responseMs === null ? (
                    <span className="text-slate-600">{t('overview.noData')}</span>
                  ) : (
                    <span className={site.responseMs > 1500 ? 'font-semibold text-red-800' : ''}>
                      {site.responseMs} ms
                    </span>
                  )}
                </td>
                <td className={`${cell} hidden text-slate-700 xl:table-cell`}>
                  {site.lastCheckAt
                    ? formatDateTime(site.lastCheckAt, i18n.language, timeZone)
                    : t('site.notChecked')}
                </td>
                <td className={`${cell} hidden text-right sm:table-cell`}>
                  <LinkButton to={`/sites/${site.id}`} variant="secondary" size="sm">
                    {t('overview.card.details')}
                  </LinkButton>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

type Group = 'up' | 'disturbed' | 'down' | 'paused' | 'unknown';
const GROUPS: readonly Group[] = ['up', 'disturbed', 'down', 'paused', 'unknown'];
const GROUP_COLOR: Record<Group, { stroke: string; dot: string }> = {
  up: { stroke: '#16a34a', dot: 'bg-green-600' },
  disturbed: { stroke: '#fbbf24', dot: 'bg-amber-400' },
  down: { stroke: '#b91c1c', dot: 'bg-red-700' },
  paused: { stroke: '#94a3b8', dot: 'bg-slate-400' },
  unknown: { stroke: '#e2e8f0', dot: 'bg-slate-200' },
};
const groupOf = (s: Site): Group =>
  s.status === 'paused'
    ? 'paused'
    : s.uptimeState === 'up'
      ? 'up'
      : s.uptimeState === 'down'
        ? 'down'
        : s.uptimeState === 'degraded' || s.uptimeState === 'suspect'
          ? 'disturbed'
          : 'unknown';

/**
 * The situation of all sites at a glance (T-88): a ring by state with the sites that are down,
 * and the last 24 hours. On wide screens it stands right of the list.
 */
function Situation({ sites }: { sites: readonly Site[] }) {
  const { t, i18n } = useTranslation();
  const counts = Object.fromEntries(
    GROUPS.map((g) => [g, sites.filter((s) => groupOf(s) === g).length]),
  ) as Record<Group, number>;
  const down = sites.filter((s) => groupOf(s) === 'down');
  const active = sites.filter((s) => s.status !== 'paused');
  const uptime = hoursUptime(active.flatMap((s) => s.uptimeHours));
  const withOutage = active.filter((s) => s.uptimeHours.some((h) => h.down)).length;
  const scored = sites.filter((s) => s.healthScore !== null);
  const avgScore = scored.length
    ? Math.round(scored.reduce((n, s) => n + (s.healthScore ?? 0), 0) / scored.length)
    : null;
  const urgentCount = sites.reduce((n, s) => n + urgent(s), 0);
  // Ring segments on a circle of circumference 100, starting at the top.
  let offset = 25;
  const segments = GROUPS.filter((g) => counts[g] > 0).map((g) => {
    const share = (counts[g] / sites.length) * 100;
    const segment = { g, share, offset };
    offset -= share;
    return segment;
  });
  const figures: [string, string][] = [
    [
      t('overview.situation.uptime24h'),
      uptime === null ? '–' : `${formatNumber(uptime, i18n.language, 2)} %`,
    ],
    [t('overview.situation.outages24h'), String(withOutage)],
    [t('overview.summary.score'), avgScore === null ? '–' : String(avgScore)],
    [t('overview.summary.urgent'), String(urgentCount)],
  ];
  return (
    <section
      aria-label={t('overview.situation.label')}
      className="grid gap-4 md:grid-cols-2 xl:grid-cols-1"
    >
      <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="text-lg font-semibold">{t('overview.situation.now')}</h2>
        <div className="flex flex-wrap items-center gap-5">
          <svg
            viewBox="0 0 42 42"
            className="size-32 shrink-0"
            role="img"
            aria-label={t('overview.situation.ringSr', {
              count: sites.length,
              up: counts.up,
              down: counts.down,
            })}
          >
            <circle cx="21" cy="21" r="15.9155" fill="none" stroke="#f1f5f9" strokeWidth="5" />
            {segments.map(({ g, share, offset: o }) => (
              <circle
                key={g}
                cx="21"
                cy="21"
                r="15.9155"
                fill="none"
                stroke={GROUP_COLOR[g].stroke}
                strokeWidth="5"
                strokeDasharray={`${share} ${100 - share}`}
                strokeDashoffset={o}
              />
            ))}
            <text x="21" y="22.5" textAnchor="middle" fontSize="9" fontWeight="800" fill="#0f172a">
              {sites.length}
            </text>
            <text x="21" y="28" textAnchor="middle" fontSize="3.6" fill="#334155">
              {t('overview.summary.sites')}
            </text>
          </svg>
          <ul className="flex flex-col gap-1.5 text-sm">
            {GROUPS.filter((g) => counts[g] > 0 || g === 'up' || g === 'down').map((g) => (
              <li key={g} className="flex items-center gap-2">
                <span aria-hidden="true" className={`size-2.5 rounded-sm ${GROUP_COLOR[g].dot}`} />
                <strong className="tabular-nums">{counts[g]}</strong>
                {t(`overview.situation.groups.${g}`)}
              </li>
            ))}
          </ul>
        </div>
        <p className="sr-only">
          {t('overview.summary.online', { count: counts.up, total: active.length })}
        </p>
        {down.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {down.map((s) => (
              <li key={s.id}>
                <Link
                  to={`/sites/${s.id}`}
                  className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm font-semibold text-red-900 hover:underline"
                >
                  <span aria-hidden="true" className="size-2 shrink-0 rounded-full bg-red-700" />
                  {t('overview.situation.isDown', { name: s.name })}
                </Link>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <div className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-5">
        <h2 className="text-lg font-semibold">{t('overview.situation.last24h')}</h2>
        <dl className="grid grid-cols-2 gap-4">
          {figures.map(([label, value]) => (
            <div key={label}>
              <dt className="text-sm text-slate-700">{label}</dt>
              <dd className="mt-1 text-2xl font-extrabold tabular-nums">{value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

/** Colour of the band on top of a card by the site's grade (T-81, owner 04.10.2026). */
const BAND: Record<string, string> = {
  A: 'bg-green-600',
  B: 'bg-lime-500',
  C: 'bg-amber-400',
  D: 'bg-orange-500',
  E: 'bg-red-500',
  F: 'bg-red-700',
};

/** Unreachable is red whatever the grade; paused and not yet graded are grey. */
const bandOf = (state: MonitorState, grade: string | null) =>
  state === 'down'
    ? 'bg-red-700'
    : state === 'paused' || !grade
      ? 'bg-slate-300'
      : (BAND[grade] ?? 'bg-slate-300');

const GRADE_STYLE: Record<string, string> = {
  A: 'bg-green-100 text-green-900',
  B: 'bg-lime-100 text-lime-900',
  C: 'bg-amber-100 text-amber-950',
  D: 'bg-orange-100 text-orange-950',
  E: 'bg-red-100 text-red-900',
  F: 'bg-red-200 text-red-950',
};

/** Card variant A "Status-Kopf" (T-81): state band, big score, three key figures. */
function SiteCard({ site }: { site: Site }) {
  const { t, i18n } = useTranslation();
  const timeZone = useMe().data?.account.timezone;
  const state: MonitorState = site.status === 'paused' ? 'paused' : site.uptimeState;
  const trend = site.healthTrend;
  const delta =
    trend.length >= 2 ? (trend[trend.length - 1]?.score ?? 0) - (trend[0]?.score ?? 0) : null;
  const h = site.hosting;
  const total = openTotal(site);
  const hot = urgent(site);
  const sslWarn = site.sslDaysLeft !== null && site.sslDaysLeft < 14;
  const slow = site.responseMs !== null && site.responseMs > 1500;
  const days = (n: number | null) =>
    n === null ? t('overview.noData') : t('overview.card.daysLeft', { count: n });
  const lastCheck = site.lastCheckAt
    ? t('overview.card.lastCheck', {
        time: formatDateTime(site.lastCheckAt, i18n.language, timeZone),
      })
    : t('site.notChecked');
  const kpis: [string, ReactNode, boolean][] = [
    [
      t('overview.card.uptimeShort'),
      site.uptime30d === null
        ? t('overview.noData')
        : `${formatNumber(site.uptime30d, i18n.language, 2)} %`,
      false,
    ],
    [
      t('overview.card.response'),
      site.responseMs === null ? t('overview.noData') : `${site.responseMs} ms`,
      slow,
    ],
    [t('overview.card.ssl'), days(site.sslDaysLeft), sslWarn],
  ];
  return (
    <article
      className={`flex h-full flex-col overflow-hidden rounded-xl border bg-white shadow-sm ${state === 'down' ? 'border-red-200' : 'border-slate-200'}`}
    >
      <div
        aria-hidden="true"
        data-band={site.healthGrade ?? ''}
        className={`h-1.5 ${bandOf(state, site.healthGrade)}`}
      />
      <div className="flex flex-1 flex-col gap-4 p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-lg font-bold">
              <Link
                to={`/sites/${site.id}`}
                aria-label={t('overview.open', { name: site.name })}
                className="break-words text-slate-900 hover:underline focus-visible:outline-2 focus-visible:outline-blue-700"
              >
                {site.name}
              </Link>
            </h2>
            <p className="break-all text-sm text-slate-700">
              {site.host}
              {site.cmsDetected ? (
                <>
                  {' · '}
                  <span>{site.cmsDetected}</span>
                </>
              ) : null}
            </p>
          </div>
          <StatusBadge state={state} />
        </div>

        <PulseCell site={site} />

        {state === 'down' ? (
          <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-900">
            <strong>{t('overview.card.downNow')}</strong> {lastCheck}
          </p>
        ) : null}

        <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
          <div
            role="img"
            aria-label={
              site.healthScore === null || !site.healthGrade
                ? t('overview.card.noScore')
                : t('overview.card.scoreSr', { score: site.healthScore, grade: site.healthGrade })
            }
            className="flex items-baseline gap-2"
          >
            {site.healthScore === null ? (
              <span className="text-lg font-semibold text-slate-700">
                {t('overview.card.noScore')}
              </span>
            ) : (
              <>
                <span className="text-5xl leading-none font-extrabold tracking-tight tabular-nums">
                  {site.healthScore}
                </span>
                <span className="text-slate-700">/ 100</span>
                {site.healthGrade ? (
                  <span
                    className={`rounded-md px-2.5 py-0.5 font-extrabold ${GRADE_STYLE[site.healthGrade] ?? 'bg-slate-100 text-slate-900'}`}
                  >
                    {site.healthGrade}
                  </span>
                ) : null}
              </>
            )}
          </div>
          {delta !== null ? (
            <span
              className={`text-sm font-semibold ${delta > 0 ? 'text-green-800' : delta < 0 ? 'text-red-800' : 'text-slate-700'}`}
            >
              {delta === 0
                ? t('overview.card.deltaSame')
                : t(delta > 0 ? 'overview.card.deltaUp' : 'overview.card.deltaDown', {
                    points: Math.abs(delta),
                  })}
            </span>
          ) : null}
        </div>

        <dl className="grid grid-cols-3 gap-2">
          {kpis.map(([label, value, warn]) => (
            <div
              key={label}
              className={`min-w-0 rounded-lg px-3 py-2 ${warn ? 'bg-red-50' : 'bg-slate-50'}`}
            >
              <dt className={`text-xs ${warn ? 'text-red-900' : 'text-slate-700'}`}>{label}</dt>
              <dd
                className={`font-bold break-words tabular-nums ${warn ? 'text-red-800' : 'text-slate-900'}`}
              >
                {value}
              </dd>
            </div>
          ))}
        </dl>

        <div className="grid gap-4 sm:grid-cols-2">
          <HealthTrend points={trend} />
          <UptimeStrip days={site.uptimeDays} today={new Date()} />
        </div>

        <div>
          <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-2 text-sm">
            <h3 className="font-semibold text-slate-900">
              {total === 0
                ? t('overview.problems')
                : t('overview.card.openCount', { count: total })}
            </h3>
            {hot > 0 ? (
              <Link
                to={`/sites/${site.id}/findings`}
                className="font-semibold text-red-800 hover:underline"
              >
                {t('overview.table.urgent', { count: hot })} →
              </Link>
            ) : null}
          </div>
          <SeverityBar siteId={site.id} counts={site.openFindings} order={SEVERITIES} />
        </div>

        <div className="mt-auto flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-3">
          <div className="min-w-0 text-sm text-slate-700">
            <p>{lastCheck}</p>
            <p className="break-words">
              {h?.country ? (
                <span title={`IP ${h.ip}`}>
                  <span aria-hidden="true">{flagOf(h.country)} </span>
                  {regionName(h.country, i18n.language)}
                  {h.cdn ? ` · ${t('overview.card.viaCdn', { cdn: h.cdn })}` : null}
                </span>
              ) : null}
              {h?.org ? (
                <>
                  {h.country ? ' · ' : null}
                  <span title={h.asn ? `AS${h.asn}` : undefined}>{h.org}</span>
                </>
              ) : null}
              {site.domainDaysLeft !== null && site.domainDaysLeft < 30 ? (
                <span className="font-semibold text-red-800">
                  {' · '}
                  {t('overview.card.domain')}: {days(site.domainDaysLeft)}
                </span>
              ) : null}
            </p>
          </div>
          <span className="flex flex-wrap gap-2">
            <LinkButton to={`/sites/${site.id}/findings`} variant="secondary" size="sm">
              {t('overview.card.findings')}
            </LinkButton>
            <LinkButton to={`/sites/${site.id}`} variant="primary" size="sm">
              {t('overview.card.details')}
            </LinkButton>
          </span>
        </div>
      </div>
    </article>
  );
}
